/**
 * Hjelpemetoder for temporal-API et
 * Se https://tc39.es/proposal-temporal for dokumentasjon av temporal APIet
 */

import {Temporal} from "temporal-polyfill";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {dateSomPlainDate, dateSomZonedDateTime} from "felles-ng2/støttefunksjoner/temporalTilDateKonverteringer";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Lokaleinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {isValid, parse} from "date-fns";
import {Debugger} from "debug";
import {DurationSerialisert, PlainDateSerialisert, PlainTimeSerialisert, ZonedDateTimeSerialisert} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {PlainDatePeriode} from "felles-ng2/støttefunksjoner/plainDatePerioder";
import {ZonedDateTimePeriode} from "felles-ng2/støttefunksjoner/zonedDateTimePerioder";
import {nil} from "felles-ng2/typer/nullish";
import Instant = Temporal.Instant;
import PlainDate = Temporal.PlainDate;
import PlainTime = Temporal.PlainTime;
import Duration = Temporal.Duration;
import ZonedDateTime = Temporal.ZonedDateTime;
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {Klokke} from "felles-ng2/tjenester/Klokke";

const debug: Debugger = require('debug')('temporal');

/** Datokonstant som representerer minste datoverdi, til bruk i feks i fra-til søk der fra skal være tilnærmet uendelig bakover i tid
 * Da vi antar at vi ikke har data tidligere enn 1970 brukes epoc 0 som verdi */
export const minZonedDateTime = Instant.fromEpochSeconds(0).toZonedDateTimeISO('UTC');

/** Datokonstant som representerer største datoverdi, til bruk i feks i fra-til søk der fra skal være tilnærmet uendelig fremover i tid
 * no.systemfabrikken.lederplan.delt.Dato#maxZonedDateTime er definert som +33658-09-27T01:46:40Z
 * men dette er større en hva som er mulig å representere i javascripts Date */
export const  maxZonedDateTime = lesZonedDateTimeSerialisert('4000-01-01T12:00:00Z')

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server.
 * Returnerer dato i UTC med mindre annen tidssone er angitt. */
export function lesZonedDateTimeSerialisert(zonedDateTimeStreng: ZonedDateTimeSerialisert, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTime {
    return Temporal.Instant.from(zonedDateTimeStreng).toZonedDateTimeISO(tidssone.somZoneId());
}

export function fraMillisSidenEpostTilZoneDateTime(tidIMillis: number, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTime {
    return Instant.fromEpochMilliseconds(tidIMillis).toZonedDateTimeISO(tidssone.somZoneId());
}

/** Deserialiserer til ZonedDateTime fra json-representasjon sendt fra server */
export function lesZonedDateTimeSerialisertOpt(zonedDateTimeStreng: ZonedDateTimeSerialisert | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTime | null {
    if (erNil(zonedDateTimeStreng)) return null;
    return lesZonedDateTimeSerialisert(zonedDateTimeStreng, tidssone);
}

/** Deserialiserer til ZonedDateTime fra json-representasjon / Date sendt fra server.
 * Returnerer dato i UTC med mindre annen tidssone er angitt. */
export function lesDateEllerZonedDateTimeSerialisert(dato: ZonedDateTimeSerialisert | Date, tidssone?: Tidssoneinformasjon): ZonedDateTime;
export function lesDateEllerZonedDateTimeSerialisert(dato: ZonedDateTimeSerialisert | Date | null, tidssone?: Tidssoneinformasjon): ZonedDateTime | null;
export function lesDateEllerZonedDateTimeSerialisert(dato: ZonedDateTimeSerialisert | Date | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTime | null {
    if (erNil(dato)) return null;
    if (erStreng(dato)) return lesZonedDateTimeSerialisertOpt(dato, tidssone);
    return dateSomZonedDateTime(dato, tidssone);
}


/** Serialiserer til streng fra ZonedDateTime */
export function skrivZonedDateTimeSerialisert(zonedDateTime: ZonedDateTime): ZonedDateTimeSerialisert {
    return zonedDateTime.toInstant().toString();
}

/** Serialiserer til streng fra ZonedDateTime */
export function skrivZonedDateTimeSerialisertOpt(zonedDateTime: ZonedDateTime | nil): ZonedDateTimeSerialisert | null {
    if (erNil(zonedDateTime)) return null;
    return skrivZonedDateTimeSerialisert(zonedDateTime);
}

/** Deserialiserer til PlainDate fra json-representasjon sendt fra server */
export function lesPlainDateSerialisert(plainDateStreng: PlainDateSerialisert): PlainDate {
    return PlainDate.from(plainDateStreng);
}

/** Deserialiserer til PlainDate fra json-representasjon sendt fra server */
export function lesPlainDateSerialisertOpt(plainDateStreng: PlainDateSerialisert | nil): PlainDate | null {
    if (erNil(plainDateStreng)) return null;
    return lesPlainDateSerialisert(plainDateStreng)
}

/** Konverterer angitt datostreng til en {@link PlainDate} instans.
 * Forventet format leses fra angitt lokale (se {@link Lokaleinformasjon#datoformat}
 * Returnerer null dersom datostreng ikke lot seg tolke
 * @deprecated bruk lesPlainDateSerialisert */
export function lesPlainDateFraStreng(datostreng: string, lokale: Lokaleinformasjon): PlainDate | null {
    const jsDate = parse(datostreng, lokale.datoformat(), new Date());
    if (!isValid(jsDate)) {
        debug(`parse: angitt verdi '${datostreng}' lot seg ikke parse som Date med format ${(lokale.datoformat())}`)
        return null;
    }
    const plainDate = dateSomPlainDate(jsDate);
    debug(`parse: leser '${datostreng}' med forventet format ${(lokale.datoformat())} -> ${plainDate?.toString()}`)
    return plainDate;
}

/** Serialiserer til streng fra PlainDate */
export function skrivPlainDateSerialisert(dato: PlainDate): PlainDateSerialisert {
    return dato.toString();
}

/** Serialiserer til streng fra PlainDate */
export function skrivPlainDateSerialisertOpt(dato: PlainDate | nil): PlainDateSerialisert | null {
    if (erNil(dato)) return null;
    return skrivPlainDateSerialisert(dato);
}

/** Deserialiserer til PlainTime fra json-representasjon sendt fra server */
export function lesPlainTimeSerialisert(plainTimeStreng: PlainTimeSerialisert): PlainTime {
    return PlainTime.from(plainTimeStreng);
}

/** Deserialiserer til PlainTime fra json-representasjon sendt fra server */
export function lesPlainTimeSerialisertOpt(plainTimeStreng: PlainTimeSerialisert | nil): PlainTime | null {
    if (erNil(plainTimeStreng)) return null;
    return lesPlainTimeSerialisert(plainTimeStreng);
}

/** Serialiserer til streng fra PlainTime */
export function skrivPlainTimeSerialisert(klokkeslett: PlainTime): PlainTimeSerialisert {
    return klokkeslett.toJSON();
}

/** Serialiserer til streng fra PlainTime */
export function skrivPlainTimeSerialisertOpt(klokkeslett: PlainTime | nil): PlainTimeSerialisert | null {
    if (erNil(klokkeslett)) return null;
    return skrivPlainTimeSerialisert(klokkeslett);
}

/** Deserialiserer til Duration fra json-representasjon sendt fra server */
export function lesDurationSerialisert(durationStreng: DurationSerialisert): Duration {
    return Duration.from(durationStreng);
}

/** Deserialiserer til Duration fra json-representasjon sendt fra server */
export function lesDurationSerialisertOpt(durationStreng: DurationSerialisert | nil): Duration | null {
    if (erNil(durationStreng)) return null;
    return lesDurationSerialisert(durationStreng);
}

/** Serialiserer til streng fra Duration */
export function skrivDurationSerialisert(klokkeslett: Duration): DurationSerialisert {
    return klokkeslett.toJSON();
}

/** Serialiserer til streng fra Duration */
export function skrivDurationSerialisertOpt(klokkeslett: Duration | nil): DurationSerialisert | null {
    if (erNil(klokkeslett)) return null;
    return skrivDurationSerialisert(klokkeslett);
}


/** Returnerer 'nå' somZonedDateTime.
 * @deprecated bruke Klokke tjenesten! */
export function nåSomZonedDateTime(): ZonedDateTime {
    //inntil vi har fått impl mockbar 'nå' for temporal lener vi oss på Date
    return dateSomZonedDateTime(new Date())!;
}

/** Returnerer 'nå' PlainDate.
 * @deprecated bruke Klokke tjenesten! */
export function nåSomPlainDate(tidssone: Tidssoneinformasjon): PlainDate {
    //inntil vi har fått impl mockbar 'nå' for temporal lener vi oss på Date
    return plainDateFraZonedDateTime(nåSomZonedDateTime(), tidssone);
}

/** Sann dersom de to ZonedDateTime instansene er like.
 * MERK at {@link ZonedDateTime#equals} ikke tollerer null som argument,
 * det gjør derimot erLikeZonedDateTime(..) */
export function erLikeZonedDateTime(a: ZonedDateTime | null, b: ZonedDateTime | null): boolean {
    if (a === b) return true;
    if (erNil(a) || erNil(b)) return false;
    return a.equals(b);
}

/** Sann dersom de to PlainDate instansene er like.
 * MERK at {@link PlainDate#equals} ikke tollerer null som argument,
 * det gjør derimot erLikePlainDate(..) */
export function erLikePlainDate(a: PlainDate | null, b: PlainDate | null): boolean {
    if (a === b) return true;
    if (erNil(a) || erNil(b)) return false;
    return a.equals(b);
}

/** Sann dersom de to ZonedDateTime instansene representerer samme dag.
 * Sammenlikner dato-delen etter å ha endret til UTC tidssone for begge angitte instanser. */
export function erSammeDagZonedDateTime(a: ZonedDateTime | null, b: ZonedDateTime | null): boolean {
    if (a === b) return true;
    if (erNil(a) || erNil(b)) return false;
    return a.withTimeZone(b.getTimeZone()).toPlainDate().equals(b.toPlainDate());
}

/** Sammenlikner a og b.
 * Nil-verdi sortert etter ikke-nil-verdi.
 * Returnerer negativ dersom a er tidligere enn b, 0 dersom like, positiv dersom a er senere enn b. */
export function sammenliknZonedDateTime(a: ZonedDateTime | nil, b: ZonedDateTime | nil):number{
    if (a === b) return 0;
    if (erNil(a)) return 1;
    if (erNil(b)) return -1;
    var diff = a.until(b).sign;
    return diff == 0 ? 0 : diff * -1;
}

/** Returnerer sann dersom a er tidligere enn b.
 * Eks: erZonedDateTimeTidligereEnn(10.01.2017 12:00, 11.01.2017 12:00) == true */
export function erZonedDateTimeTidligereEnn(a: ZonedDateTime, b: ZonedDateTime): boolean {
    return a.until(b).sign > 0;
}

/** Returnerer sann dersom datoOgTid er passert. */
export function erZonedDateTimePassert(datoOgTid: ZonedDateTime, klokke:Klokke): boolean {
    return erZonedDateTimeTidligereEnn(datoOgTid, klokke.nåSomZonedDateTime());
}

/** Returnerer sann dersom a er tidligere enn b.
 * Eks: erPlainDateTidligereEnn(10.01.2017, 11.01.2017) == true  */
export function erPlainDateTidligereEnn(a: PlainDate, b: PlainDate): boolean {
    return a.until(b).sign > 0;
}


/** Returnerer sann dersom a er senere enn b.
 * Eks: erZonedDateTimeSenereEnn(12.01.2017 12:00, 11.01.2017 12:00) == true */
export function erZonedDateTimeSenereEnn(a: ZonedDateTime, b: ZonedDateTime): boolean {
    return a.until(b).sign < 0;
}

/** Returnerer sann dersom a er senere enn b.
 * Eks: erPlainDateSenereEnn(12.01.2017, 11.01.2017) == true  */
export function erPlainDateSenereEnn(a: PlainDate, b: PlainDate): boolean {
    return a.until(b).sign < 0;
}

/** Returnerer sann dersom a og b er i samme år og måned */
export function erSammeÅrOgMånedZonedDateTime(a: ZonedDateTime, b: ZonedDateTime): boolean {
    const bMedTidssone = b.withTimeZone(a.timeZoneId);
    return a.year == bMedTidssone.year && a.month == bMedTidssone.month;
}

/** Returnerer sann dersom a og b er i samme år og måned */
export function erSammeÅrOgMånedPlainDate(a: PlainDate, b: PlainDate): boolean {
    return a.year == b.year && a.month == b.month;
}

/** Returnerer sann dersom a er tidligere enn b.
 * Eks: erPlainTimeTidligereEnn(10:00, 12:00) == true */
export function erPlainTimeTidligereEnn(a: PlainTime, b: PlainTime): boolean {
    return a.until(b).sign > 0;
}

/** Returnerer sann dersom a er tidligere enn eller lik b.
 * Eks: erPlainTimeTidligereEnnEllerLik(10:00, 12:00) == true */
export function erPlainTimeTidligereEnnEllerLik(a: PlainTime, b: PlainTime): boolean {
    return a.until(b).sign >= 0;
}

export function tomDuration(): Duration {
    return new Duration();
}

export function durationFraMinutter(minutter: number | nil): Duration | null {
    if (erNil(minutter)) return null;
    return Duration.from({minutes: minutter});
}

export function durationTilMinutter(varighet: Duration): number;
export function durationTilMinutter(varighet: Duration | nil): number | null;
export function durationTilMinutter(varighet: Duration | nil): number | null {
    if (erNil(varighet)) return null;
    return Math.round(varighet.total('minutes'));
}

export function durationFraPeriode(periode: ZonedDateTimePeriode): Duration {
    return periode.start.until(periode.slutt);
}

/** Gjør om angitt PlainDate til en ZonedDateTime på samme dato med klokkeslett satt til 00:00 (start på dagen) i angitt tidssone */
export function zonedDateTimeStartPåDagenFraPlainDate(dato: PlainDate, tidssone: Tidssoneinformasjon): ZonedDateTime {
    return dato.toZonedDateTime({timeZone: tidssone.somZoneId(), plainTime: PlainTime.from('00:00')});
}

/** Gjør om angitt PlainDate til en ZonedDateTime på samme dato med klokkeslett satt til 23:59:59 (slutt på dagen) i angitt tidssone */
export function zonedDateTimeSluttPåDagenFraPlainDate(dato: PlainDate, tidssone: Tidssoneinformasjon): ZonedDateTime {
    return dato.toZonedDateTime({timeZone: tidssone.somZoneId(), plainTime: new PlainTime(23, 59, 59, 999)});
}

/** Gjør om angitt ZonedDateTime til PlainDate i angitt tidssone */
export function plainDateFraZonedDateTime(datoOgTid: ZonedDateTime, tidssone: Tidssoneinformasjon): PlainDate {
    return datoOgTid.withTimeZone(tidssone.somZoneId()).toPlainDate();
}

/** Returnerer kopi av dato med dag satt til første dag i måneden klokken 00:00 */
export function zonedDateTimeFørsteDagIMåneden(dato: ZonedDateTime): ZonedDateTime {
    return zonedDateTimeStartPåDagen(dato.with({day: 1}));
}

/** Returnerer kopi av dato med dag satt til siste dag i måneden klokken 23:59:59:999 */
export function zonedDateTimeSisteDagIMåneden(dato: ZonedDateTime): ZonedDateTime {
    return zonedDateTimeSluttPåDagen(dato.with({day: dato.daysInMonth}));
}

export function zonedDateTimeStartPåDagen(dato: ZonedDateTime): ZonedDateTime {
    return dato.withPlainTime(PlainTime.from('00:00:00'));
}

export function zonedDateTimeSluttPåDagen(dato: ZonedDateTime): ZonedDateTime {
    return dato.withPlainTime(new PlainTime(23, 59, 59, 999));
}

/** Returnerer array med ett innslag per måned i angitt periode, dag er satt i 1 i returnerte datoer */
export function månederIPlainDatePeriode(periode: PlainDatePeriode): PlainDate[] {
    const inkluderteMåneder: PlainDate[] = [];
    let dato = periode.start.with({day: 1});
    while ((dato.year * 12 + dato.month) <= (periode.slutt.year * 12 + periode.slutt.month)) {
        inkluderteMåneder.push(dato);
        dato = dato.add({months: 1});
    }
    return inkluderteMåneder;
}

/** Returnerer klokkeslett-del av angitt datoOgKlokkeslett i angitt tidssone */
export function plainTimeFraZonedDateTime(datoOgKlokkeslett: ZonedDateTime, tidssone:Tidssoneinformasjon):PlainTime{
    return datoOgKlokkeslett.withTimeZone(tidssone.somZoneId()).toPlainTime();
}

/** Returnerer klokkeslett-del av angitt datoOgKlokkeslett i UTC */
export function plainTimeFraZonedDateTimeUTC(datoOgKlokkeslett: ZonedDateTime):PlainTime{
    return datoOgKlokkeslett.withTimeZone(Tidssoneinformasjon.UTC.somZoneId()).toPlainTime();
}

/** Returnerer en Duration med varighet satt til angitt antall minutter, 0 dersom minutter er null */
export function varighetIMinutter(minutter: number | nil): Duration {
    return Duration.from({minutes: minutter ?? 0});
}
